import {useCallback, useEffect, useState} from 'react';
import paper from 'paper';
import {START_ZOOM_PERCENTAGE, START_ZOOM_COEFFICIENT, MIN_ZOOM, MAX_ZOOM} from '@/constants/editor';

export function useCanvasViewport({containerRef, drawBackgroundCanvas, drawRulers, updateTransformUI}) {
  const [zoomLevel, setZoomLevel] = useState(START_ZOOM_PERCENTAGE);

  const centerView = useCallback(() => {
    if (!paper.view) return;
    paper.view.center = new paper.Point(0, 0);
    drawBackgroundCanvas();
    drawRulers();
  }, [drawBackgroundCanvas, drawRulers]);

  const resetZoom = useCallback(() => {
    if (!paper.view) return;
    paper.view.zoom = START_ZOOM_COEFFICIENT;
    centerView();

    paper.project.getItems({data: {isGuide: true}}).forEach((guide) => {
      guide.strokeWidth = 1;
      guide.dashArray = [6, 6];
    });

    setZoomLevel(START_ZOOM_PERCENTAGE);
    updateTransformUI();
  }, [centerView, updateTransformUI]);

  const handleWheel = useCallback(
    (e) => {
      if (!paper.view || !paper.view.element) return;

      e.preventDefault();
      e.stopPropagation();

      const zoomFactor = 1.15;
      const oldZoom = paper.view.zoom;
      let newZoom = e.deltaY < 0 ? oldZoom * zoomFactor : oldZoom / zoomFactor;
      newZoom = Math.min(Math.max(newZoom, MIN_ZOOM), MAX_ZOOM);

      const canvasEl = paper.view.element;
      const rect = canvasEl.getBoundingClientRect();
      const mousePosition = new paper.Point(e.clientX - rect.left, e.clientY - rect.top);
      const viewPosition = paper.view.viewToProject(mousePosition);

      paper.view.zoom = newZoom;
      paper.view.center = paper.view.center.add(
        viewPosition.subtract(paper.view.viewToProject(mousePosition))
      );

      paper.project.getItems({data: {isGuide: true}}).forEach((guide) => {
        guide.strokeWidth = 1 / newZoom;
        guide.dashArray = [6 / newZoom, 6 / newZoom];
      });

      setZoomLevel(Math.round(newZoom * 100));
      drawBackgroundCanvas();
      drawRulers();
      updateTransformUI();
    },
    [drawBackgroundCanvas, drawRulers, updateTransformUI]
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener('wheel', handleWheel, {passive: false});
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [containerRef, handleWheel]);

  return {zoomLevel, centerView, resetZoom};
}